import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import PrintPlatformAttribution from "./PrintPlatformAttribution";
import type { InvoicePrintLayoutProps } from "./types";

const printSx = {
  fontFamily: '"Source Serif 4", "Georgia", "Times New Roman", serif',
  fontSize: "11px",
  lineHeight: 1.55,
  color: "#111",
  "@media print": {
    fontSize: "11px"
  }
} as const;

/**
 * Print-only invoice layout (Phase 1: mock props; Phase 2: API-backed).
 */
export const InvoicePrintLayout: React.FC<InvoicePrintLayoutProps> = ({
  businessName,
  addressLines,
  email,
  phone,
  invoiceNumber,
  billTo,
  invoiceDateDisplay,
  dueDateDisplay,
  lineItems,
  subtotal,
  discountLabel,
  discountAmount,
  taxLabel,
  taxAmount,
  totalDue,
  thankYouMessage = "Thank you for your business."
}) => {
  const formatCurrency = useFormatCurrency();
  const showDiscountRow = discountLabel != null && discountAmount != null;

  return (
    <Box sx={printSx}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          borderBottom: "2px solid #111",
          pb: 2,
          mb: 2.5
        }}
      >
        <Box>
          <Typography
            component="div"
            sx={{ fontSize: "20px", fontWeight: 700, letterSpacing: "-0.02em" }}
          >
            {businessName}
          </Typography>
          {addressLines.map((line) => (
            <Typography
              key={line}
              component="div"
              sx={{ fontSize: "10px", color: "#333" }}
            >
              {line}
            </Typography>
          ))}
          {email ? (
            <Typography
              component="div"
              sx={{ fontSize: "10px", color: "#333", mt: 0.25 }}
            >
              {email}
            </Typography>
          ) : null}
          {phone ? (
            <Typography
              component="div"
              sx={{ fontSize: "10px", color: "#333" }}
            >
              {phone}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography
            component="div"
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em"
            }}
          >
            Invoice
          </Typography>
          <Typography
            component="div"
            sx={{ fontSize: "22px", fontWeight: 700, mt: 0.5 }}
          >
            {invoiceNumber}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          mb: 2.5,
          fontSize: "10px"
        }}
      >
        <Box>
          <Typography
            component="div"
            sx={{
              fontWeight: 700,
              mb: 0.5,
              textTransform: "uppercase",
              fontSize: "9px",
              letterSpacing: "0.06em"
            }}
          >
            Bill to
          </Typography>
          {billTo ? (
            <>
              <Typography component="div" sx={{ fontWeight: 600 }}>
                {billTo.name}
              </Typography>
              {billTo.lines.map((line) => (
                <Typography key={line} component="div" sx={{ color: "#333" }}>
                  {line}
                </Typography>
              ))}
            </>
          ) : (
            <Typography component="div" sx={{ color: "#666" }}>
              —
            </Typography>
          )}
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Box sx={{ mb: 1 }}>
            <Typography
              component="div"
              sx={{
                color: "#555",
                fontSize: "9px",
                textTransform: "uppercase"
              }}
            >
              Invoice date
            </Typography>
            <Typography component="div" sx={{ fontWeight: 600 }}>
              {invoiceDateDisplay}
            </Typography>
          </Box>
          {dueDateDisplay ? (
            <Box>
              <Typography
                component="div"
                sx={{
                  color: "#555",
                  fontSize: "9px",
                  textTransform: "uppercase"
                }}
              >
                Due date
              </Typography>
              <Typography component="div" sx={{ fontWeight: 600 }}>
                {dueDateDisplay}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>

      <Table
        size="small"
        sx={{
          width: "100%",
          fontSize: "10px",
          mb: 2,
          "& .MuiTableCell-root": { borderColor: "#e5e5e5", py: 0.85, px: 0.5 }
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                textAlign: "left",
                fontWeight: 700,
                borderBottomColor: "#111"
              }}
            >
              Description
            </TableCell>
            <TableCell
              sx={{
                textAlign: "right",
                fontWeight: 700,
                width: "2.5rem",
                borderBottomColor: "#111"
              }}
            >
              Qty
            </TableCell>
            <TableCell
              sx={{
                textAlign: "right",
                fontWeight: 700,
                width: "5.5rem",
                borderBottomColor: "#111"
              }}
            >
              Rate
            </TableCell>
            <TableCell
              sx={{
                textAlign: "right",
                fontWeight: 700,
                width: "5.5rem",
                borderBottomColor: "#111"
              }}
            >
              Amount
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lineItems.map((row) => (
            <TableRow key={row.description}>
              <TableCell sx={{ verticalAlign: "top", pr: 1 }}>
                {row.description}
              </TableCell>
              <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {row.qty}
              </TableCell>
              <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {formatCurrency(row.rate)}
              </TableCell>
              <TableCell
                sx={{
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  fontWeight: 600
                }}
              >
                {formatCurrency(row.amount)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ maxWidth: "280px", ml: "auto", fontSize: "10px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.4 }}>
          <Typography component="span">Subtotal</Typography>
          <Typography component="span">{formatCurrency(subtotal)}</Typography>
        </Box>
        {showDiscountRow ? (
          <Box
            sx={{ display: "flex", justifyContent: "space-between", py: 0.4 }}
          >
            <Typography component="span">{discountLabel}</Typography>
            <Typography component="span">
              {formatCurrency(discountAmount!)}
            </Typography>
          </Box>
        ) : null}
        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.4 }}>
          <Typography component="span">{taxLabel}</Typography>
          <Typography component="span">{formatCurrency(taxAmount)}</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            py: 1,
            mt: 0.5,
            borderTop: "2px solid #111",
            fontSize: "13px"
          }}
        >
          <Typography component="span" sx={{ fontWeight: 700 }}>
            Total due
          </Typography>
          <Typography component="span" sx={{ fontWeight: 700 }}>
            {formatCurrency(totalDue)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography
          component="div"
          sx={{ fontStyle: "italic", fontSize: "10px" }}
        >
          {thankYouMessage}
        </Typography>
        <PrintPlatformAttribution />
      </Box>
    </Box>
  );
};

export default InvoicePrintLayout;
